/**
 * M0 renderer: one three.js scene, clean lighting with soft shadows.
 * Post-processing lives in PostFx, which builds its composer on this renderer.
 *
 * Camera framing is owned by the CameraRig ('arena-follow' gameplay view plus
 * the locked 'chamber' fixed view, LOCKED-DESIGN 2.2); render-side juice
 * (shake, FOV punch, squash) stays here and layers on the rig's pose. The
 * input mapper reads rig.inputYaw so W = up-screen holds in EVERY view.
 */
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import type { PlayerState } from "../player/CharacterController.js";
import type { PresentationSettings, QualityLevel } from "@host/types.js";
import { ARENA_GEOMETRY } from "../world/GrayBoxArena.js";
import type { ArenaDefinition, ArenaRuntimeGeometry } from "../world/ArenaDefinition.js";
import { CAMERA_BOUNDS_SKIN, CameraRig, clampIntoBounds, fixedViewYaw } from "./CameraRig.js";
import {
  applyHeroQuality,
  createHeroAsset,
  resolveHeroGlowMaterial,
  upgradeHeroAsset,
  type HeroAsset,
} from "./HeroAssets.js";
import { GRADE_BAND_LIGHTING, HERO_THEME, LIGHTING_RIG, QUALITY_TIERS, SCENE_ENVIRONMENT } from "./theme.js";

export class SceneRenderer {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  /** public: PostFx builds its composer on this */
  readonly renderer: THREE.WebGLRenderer;

  /** player avatar: the hero sanitation-unit robot (LOCKED-DESIGN 18.3) */
  readonly playerMesh: THREE.Group;
  /** hero asset behind the GLTF-ready seam; procedural until a .glb exists */
  private playerHero: HeroAsset;
  /** squash-stretch node (`rig:flex`) — the whole body flexes like the capsule did */
  private playerBody: THREE.Object3D;
  /** expressive head unit — yaw/pitch eases toward the aim point */
  private playerHead: THREE.Object3D | null = null;
  /** articulated beam-emitter arm/dish — tracks the held load */
  private playerEmitter: THREE.Object3D | null = null;
  private playerEmitterGlow: THREE.MeshStandardMaterial | null = null;
  /** live aim state fed by the Engine (updateAim) each frame */
  private readonly aimTarget = new THREE.Vector3(0, 1, 4);
  private aimHolding = false;
  private disposed = false;

  /** named camera views + blend: 'arena-follow' gameplay + 'chamber' fixed (2.2) */
  readonly rig = new CameraRig();
  /** enclosure the camera may not leave (PLAYTEST F6); re-applied after shake */
  private readonly cameraBounds: ArenaRuntimeGeometry["floorExtents"];

  // quality tier state (LOCKED-DESIGN 13.1 scalable visuals)
  private qualityLevel: QualityLevel = "high";
  /** raw devicePixelRatio from the host; the active tier caps it */
  private rawDpr: number;

  // scratch
  readonly scratchPos = new THREE.Vector3();
  readonly scratchQuat = new THREE.Quaternion();

  // juice state (render-only)
  private shakeAmount = 0;
  private fovKick = 0;
  private impactKick = 0;
  private squashAmount = 0;
  private readonly kickVel = new THREE.Vector3();
  /** live presentation knobs (LOCKED-DESIGN 12.3/18.5); every impulse routes through them */
  private presentation: PresentationSettings | null = null;
  /**
   * prefers-reduced-motion stills EVERY camera-motion impulse (shake, kick,
   * FOV punches) regardless of the stored screenshake knob — vestibular
   * safety outranks juice (host/types.ts effect-layer contract).
   */
  private readonly reducedMotionQuery =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
  // grade-band ambience drift (LOCKED-DESIGN 18.4/2.2): gentle warmth/cleanliness
  // shift across key/rim/hemisphere-bounce/room-haze — never darker, token-driven
  private keyLight!: THREE.DirectionalLight;
  private rimLight!: THREE.DirectionalLight;
  private hemiLight!: THREE.HemisphereLight;
  private readonly keyTarget = new THREE.Color(LIGHTING_RIG.key.color);
  private readonly rimTarget = new THREE.Color(LIGHTING_RIG.rim.color);
  private readonly groundTarget = new THREE.Color(LIGHTING_RIG.hemisphere.ground);
  private readonly hazeTarget = new THREE.Color(SCENE_ENVIRONMENT.fog.color);
  private readonly bgTarget = new THREE.Color(SCENE_ENVIRONMENT.background);
  private readonly bgColor = new THREE.Color(SCENE_ENVIRONMENT.background);

  /** Wire the live presentation-settings object (Engine passes ctx.presentation). */
  setPresentation(settings: PresentationSettings): void {
    this.presentation = settings;
  }

  /** Camera-motion impulse scale: the screenshake knob, zeroed under reduced motion. */
  private motionScale(): number {
    if (this.reducedMotionQuery?.matches) return 0;
    return this.presentation ? Math.max(0, Math.min(1, this.presentation.screenshakeIntensity)) : 1;
  }

  /** Impulse screenshake (max wins, exp decay). Scaled by the motion knobs. */
  shake(strength: number): void {
    this.shakeAmount = Math.max(this.shakeAmount, strength * this.motionScale());
  }

  /** Directional camera kick (blast knockback reads as a shove, not just noise). */
  kick(directionX: number, directionZ: number, strength: number): void {
    const scaled = strength * this.motionScale();
    const length = Math.hypot(directionX, directionZ) || 1;
    this.kickVel.x += (directionX / length) * scaled;
    this.kickVel.z += (directionZ / length) * scaled;
  }

  /** Quick FOV widen for speed moments (dash, portal exit). Camera motion — knob-scaled. */
  fovPunch(amount: number): void {
    this.fovKick = Math.max(this.fovKick, amount * this.motionScale());
  }

  /**
   * Render-side impact beat (LOCKED-DESIGN 18.4 hitstop redesign): a brief
   * FOV tighten + player squash. Fixed steps NEVER pause for this. The FOV
   * component is camera motion (knob-scaled, stilled under reduced motion);
   * the avatar squash is object-level feedback and survives, so the beat
   * always reads.
   */
  impactPunch(amount: number): void {
    this.impactKick = Math.max(this.impactKick, amount * this.motionScale());
    this.squashAmount = Math.max(this.squashAmount, Math.min(1, amount * 0.12));
  }

  /** Landing squash on the player capsule. */
  landSquash(intensity: number): void {
    this.squashAmount = Math.max(this.squashAmount, Math.min(1, intensity));
  }

  /**
   * Sanitation Grade band changed (LOCKED-DESIGN 18.4/2.2): drift the whole
   * clean-industrial ambience — key/rim tint, hemisphere concrete bounce, and
   * the enclosed-room haze (background + fog) — through theme tokens. Every
   * band value stays light, so the drift never reduces object visibility.
   */
  setGradeBand(band: "C" | "B" | "A" | "S"): void {
    const tint = GRADE_BAND_LIGHTING[band];
    this.keyTarget.set(tint.key);
    this.rimTarget.set(tint.rim);
    this.groundTarget.set(tint.ground);
    this.hazeTarget.set(tint.haze);
    this.bgTarget.set(tint.haze);
  }

  constructor(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    dpr: number,
    cameraConfig: ArenaDefinition["camera"] = ARENA_GEOMETRY.camera,
    bounds: ArenaRuntimeGeometry["floorExtents"] = ARENA_GEOMETRY.floorExtents,
  ) {
    // today's gameplay camera, registered 1:1 from the arena definition block
    this.rig.addView("arena-follow", {
      kind: "follow",
      yaw: cameraConfig.yaw,
      offset: cameraConfig.offset,
      lookHeight: cameraConfig.lookHeight,
      northBias: cameraConfig.northBias,
      followRate: cameraConfig.followRate,
      lookahead: cameraConfig.lookahead,
      fov: cameraConfig.fov,
    });
    // locked second fixed world-upright chamber view (2.2/18.8 predicate #3),
    // authored in the same arena camera block; its input-basis yaw is derived
    // from the pose so the controls contract holds by construction
    this.rig.addView("chamber", {
      kind: "fixed",
      yaw: fixedViewYaw(cameraConfig.chamber.position, cameraConfig.chamber.target),
      position: cameraConfig.chamber.position,
      target: cameraConfig.chamber.target,
      fov: cameraConfig.chamber.fov,
    });
    // PLAYTEST F6: the follow boom hangs 16.5 m south of the player, so from
    // the south edge the camera used to sit 9.5 m OUTSIDE a wall that does not
    // exist — the player saw the arena from the outside with grey void filling
    // the frame. The containment box is the authored floor plan up to the
    // enclosure ceiling; fixed views are exempt (authored poses, 18.13).
    this.cameraBounds = bounds;
    this.rig.setBounds(bounds);
    // antialias off (SMAA in post), tone mapping off (ACES in post)
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: "high-performance",
    });
    this.rawDpr = dpr;
    this.renderer.setPixelRatio(Math.min(dpr, QUALITY_TIERS[this.qualityLevel].dprCap));
    this.renderer.setSize(width, height, false);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.NoToneMapping;

    // clean-industrial sealed room (18.1): a bright room-haze background with
    // fog that fades far enclosure walls INTO it — no near-black void band
    this.scene.background = this.bgColor;
    this.scene.fog = new THREE.Fog(SCENE_ENVIRONMENT.fog.color, SCENE_ENVIRONMENT.fog.near, SCENE_ENVIRONMENT.fog.far);

    // cheap IBL so metals/roughness read without any downloaded HDRI
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = SCENE_ENVIRONMENT.environmentIntensity;
    pmrem.dispose();

    this.camera = new THREE.PerspectiveCamera(cameraConfig.fov, width / height, 0.1, 200);

    // structured clean daylight-grade rig (18.1): hemisphere ambient + a single
    // shadow-casting key + non-shadow cool fill + rim. The warm furnace
    // practical (built in the arena) stays the brightest, warmest focal anchor.
    const hemi = new THREE.HemisphereLight(LIGHTING_RIG.hemisphere.sky, LIGHTING_RIG.hemisphere.ground, LIGHTING_RIG.hemisphere.intensity);
    this.scene.add(hemi);
    this.hemiLight = hemi;
    const key = new THREE.DirectionalLight(LIGHTING_RIG.key.color, LIGHTING_RIG.key.intensity);
    key.position.set(...LIGHTING_RIG.key.position);
    key.castShadow = true;
    key.shadow.mapSize.set(LIGHTING_RIG.key.shadowMapSize, LIGHTING_RIG.key.shadowMapSize);
    key.shadow.camera.left = -LIGHTING_RIG.key.shadowExtent;
    key.shadow.camera.right = LIGHTING_RIG.key.shadowExtent;
    key.shadow.camera.top = LIGHTING_RIG.key.shadowExtent;
    key.shadow.camera.bottom = -LIGHTING_RIG.key.shadowExtent;
    key.shadow.camera.far = LIGHTING_RIG.key.shadowFar;
    key.shadow.bias = LIGHTING_RIG.key.shadowBias;
    this.scene.add(key);
    this.keyLight = key;
    // cool fill — the only new light; non-shadow, so no extra shadow-pass cost
    const fill = new THREE.DirectionalLight(LIGHTING_RIG.fill.color, LIGHTING_RIG.fill.intensity);
    fill.position.set(...LIGHTING_RIG.fill.position);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(LIGHTING_RIG.rim.color, LIGHTING_RIG.rim.intensity);
    rim.position.set(...LIGHTING_RIG.rim.position);
    this.scene.add(rim);
    this.rimLight = rim;

    // player avatar: hero sanitation unit (18.3) behind the GLTF-ready seam —
    // procedural today; an authored public/models/player-unit.glb hot-swaps in
    // with zero rework here (upgradeHeroAsset resolves only when one exists)
    this.playerMesh = new THREE.Group();
    this.playerHero = createHeroAsset("player-unit");
    this.playerBody = this.playerHero.root;
    this.mountPlayerHero(this.playerHero);
    this.scene.add(this.playerMesh);
    upgradeHeroAsset("player-unit", (asset) => {
      if (this.disposed) {
        asset.dispose();
        return;
      }
      this.playerHero.dispose();
      this.mountPlayerHero(asset);
      applyHeroQuality(this.playerMesh, this.qualityLevel);
    });
  }

  /** Mount a player hero asset and bind its rig nodes (squash/head/emitter). */
  private mountPlayerHero(asset: HeroAsset): void {
    this.playerHero = asset;
    this.playerMesh.add(asset.root);
    // squash-stretch applies to the flex node; a rig-less authored model still
    // works — the whole root flexes (same visual contract, coarser pivot)
    this.playerBody = asset.hardpoints.get("rig:flex") ?? asset.root;
    this.playerHead = asset.hardpoints.get("rig:head") ?? null;
    this.playerEmitter = asset.hardpoints.get("rig:emitter") ?? null;
    this.playerEmitterGlow = resolveHeroGlowMaterial(
      asset,
      "rig:emitter",
      "robot-emitter-core",
    );
  }

  /**
   * Live aim state (Engine feeds its aimPoint + beam hold every frame): the
   * head/visor looks where the player aims and the beam-emitter dish tracks
   * the held load, glowing with beam state (LOCKED-DESIGN 18.3 personality).
   * Object-level feedback — deliberately NOT gated by the motion knobs.
   */
  updateAim(aimPoint: THREE.Vector3, holding: boolean): void {
    this.aimTarget.copy(aimPoint);
    this.aimHolding = holding;
  }

  /**
   * Interpolated player render + smoothed follow camera.
   * prev/curr are physics-step positions; alpha is the frame's blend factor.
   */
  updatePlayer(
    prev: THREE.Vector3,
    curr: THREE.Vector3,
    alpha: number,
    state: PlayerState,
    frameDt: number,
  ): void {
    this.playerMesh.position.lerpVectors(prev, curr, alpha);
    // robot body center sits at the physics-body translation; pivot is fine as-is
    this.playerMesh.rotation.y = dampAngle(this.playerMesh.rotation.y, state.facingYaw, 14, frameDt);

    // squash & stretch: dash stretch + landing squash, combined — applied to
    // the hero rig's flex node (procedural animation survives the hero swap)
    this.squashAmount *= Math.exp(-9 * frameDt);
    const stretch = state.dashing ? 1.18 : 1;
    const sy = stretch * (1 - this.squashAmount * 0.35);
    const sxz = (2 - stretch) * (1 + this.squashAmount * 0.3);
    this.playerBody.scale.set(sxz, sy, sxz);

    // expressive head + beam-emitter arm: ease toward the aim point (18.3).
    // Local yaw subtracts the body's facing so the head turns, not snaps, and
    // stays inside a believable neck range.
    const aimDx = this.aimTarget.x - this.playerMesh.position.x;
    const aimDz = this.aimTarget.z - this.playerMesh.position.z;
    const aimDistance = Math.max(Math.hypot(aimDx, aimDz), 0.001);
    const aimYaw = wrapAngle(Math.atan2(aimDx, aimDz) - this.playerMesh.rotation.y);
    if (this.playerHead) {
      const yaw = THREE.MathUtils.clamp(aimYaw, -1.15, 1.15);
      const pitch = THREE.MathUtils.clamp(Math.atan2(this.aimTarget.y - (this.playerMesh.position.y + 0.64), aimDistance), -0.55, 0.4);
      this.playerHead.rotation.y = dampAngle(this.playerHead.rotation.y, yaw, 10, frameDt);
      this.playerHead.rotation.x = dampAngle(this.playerHead.rotation.x, -pitch, 10, frameDt);
    }
    if (this.playerEmitter) {
      const yaw = THREE.MathUtils.clamp(aimYaw, -1.35, 1.35);
      const pitch = THREE.MathUtils.clamp(Math.atan2(this.aimTarget.y - (this.playerMesh.position.y + 0.34), aimDistance), -0.8, 0.7);
      this.playerEmitter.rotation.y = dampAngle(this.playerEmitter.rotation.y, yaw, 12, frameDt);
      this.playerEmitter.rotation.x = dampAngle(this.playerEmitter.rotation.x, -pitch, 12, frameDt);
    }
    if (this.playerEmitterGlow) {
      const target = this.aimHolding ? HERO_THEME.robot.emitterHold : HERO_THEME.robot.emitterIdle;
      const blend = 1 - Math.exp(-9 * frameDt);
      this.playerEmitterGlow.emissiveIntensity += (target - this.playerEmitterGlow.emissiveIntensity) * blend;
    }

    // camera framing comes from the rig (arena-follow preserves legacy math)
    const pose = this.rig.update(
      { x: this.playerMesh.position.x, z: this.playerMesh.position.z, vx: state.vx, vz: state.vz },
      frameDt,
    );
    this.camera.position.copy(pose.position);
    this.camera.lookAt(pose.target);

    // screenshake: positional noise after lookAt so aim stays stable
    this.shakeAmount *= Math.exp(-7 * frameDt);
    if (this.shakeAmount > 0.002) {
      this.camera.position.x += (Math.random() - 0.5) * this.shakeAmount;
      this.camera.position.y += (Math.random() - 0.5) * this.shakeAmount * 0.6;
      this.camera.position.z += (Math.random() - 0.5) * this.shakeAmount;
    }
    // directional kick (blast shove) decays alongside the noise shake
    this.kickVel.multiplyScalar(Math.exp(-6 * frameDt));
    if (this.kickVel.lengthSq() > 0.00001) this.camera.position.add(this.kickVel);
    // ...both of which can shove a rig-contained pose straight back through the
    // wall, so containment is re-applied on the FINAL position (PLAYTEST F6).
    // Aim is untouched: lookAt already ran, exactly as the shake contract wants.
    // Gated on the rig's own exemption: a FIXED view is authored and is never
    // clamped by the rig, so the renderer must not clamp it either (18.13).
    if (this.rig.poseClampable) {
      clampIntoBounds(this.camera.position, this.cameraBounds.min, this.cameraBounds.max, CAMERA_BOUNDS_SKIN);
    }

    // FOV punch (widen) and impact punch (tighten) layered on the rig's base FOV
    this.fovKick *= Math.exp(-8 * frameDt);
    this.impactKick *= Math.exp(-11 * frameDt);
    const fov = pose.fov + this.fovKick - this.impactKick;
    if (Math.abs(fov - this.camera.fov) > 0.01) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }

    // grade-band ambience drift: slow, gentle, never darker (2.2/18.4). Key/rim
    // tint, hemisphere concrete bounce, and the enclosed-room haze all ease
    // toward the band target so the room warms/cleans as Grade rises.
    const lightBlend = 1 - Math.exp(-1.6 * frameDt);
    this.keyLight.color.lerp(this.keyTarget, lightBlend);
    this.rimLight.color.lerp(this.rimTarget, lightBlend);
    this.hemiLight.groundColor.lerp(this.groundTarget, lightBlend);
    this.bgColor.lerp(this.bgTarget, lightBlend);
    if (this.scene.fog) (this.scene.fog as THREE.Fog).color.lerp(this.hazeTarget, lightBlend);
  }

  /** Hard-cut the follow camera (portal traversal, respawn) — no smear. */
  snapFollow(x: number, z: number): void {
    this.rig.snapFollow(x, z);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  resize(width: number, height: number, dpr: number): void {
    this.rawDpr = dpr;
    this.renderer.setPixelRatio(Math.min(dpr, QUALITY_TIERS[this.qualityLevel].dprCap));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Apply a quality tier (LOCKED-DESIGN 13.1): dpr cap, shadow pass on/off,
   * shadow-map resolution, and PCF filtering. Render-only — the caller
   * (Engine.setQuality) rebuilds PostFx for the bloom/SMAA half of the tier.
   */
  setQuality(level: QualityLevel): void {
    this.qualityLevel = level;
    const tier = QUALITY_TIERS[level];
    const type = tier.softShadows ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
    if (this.renderer.shadowMap.type !== type) {
      this.renderer.shadowMap.type = type;
      // shadow filtering is compiled into shader programs — force a refresh
      this.scene.traverse((obj) => {
        const material = (obj as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(material)) material.forEach((entry) => { entry.needsUpdate = true; });
        else if (material) material.needsUpdate = true;
      });
    }
    this.renderer.shadowMap.enabled = tier.shadows;
    this.keyLight.castShadow = tier.shadows;
    if (this.keyLight.shadow.mapSize.x !== tier.shadowMapSize) {
      this.keyLight.shadow.mapSize.set(tier.shadowMapSize, tier.shadowMapSize);
      // the render target is lazily rebuilt at the new size on the next pass
      this.keyLight.shadow.map?.dispose();
      this.keyLight.shadow.map = null;
    }
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.setPixelRatio(Math.min(this.rawDpr, tier.dprCap));
    // hero garnish (tread hubs, antenna, exhausts) drops on the low tier (13.1)
    applyHeroQuality(this.playerMesh, level);
  }

  dispose(): void {
    this.disposed = true;
    this.renderer.dispose();
    this.scene.environment?.dispose();
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach(disposeMaterial);
      else if (mat) disposeMaterial(mat);
    });
  }
}

/** Dispose a material AND any textures it owns (CanvasTextures leak otherwise). */
function disposeMaterial(material: THREE.Material): void {
  for (const value of Object.values(material)) {
    if (value instanceof THREE.Texture) value.dispose();
  }
  material.dispose();
}

/** Exponential angle damping along the shortest arc. */
function dampAngle(current: number, target: number, rate: number, dt: number): number {
  let delta = target - current;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return current + delta * (1 - Math.exp(-rate * dt));
}

/** Wrap an angle into [-π, π]. */
function wrapAngle(angle: number): number {
  let wrapped = angle;
  while (wrapped > Math.PI) wrapped -= Math.PI * 2;
  while (wrapped < -Math.PI) wrapped += Math.PI * 2;
  return wrapped;
}
